import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, Save } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { addWork } from '../api';

const WorkForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    workName: '',
    clientName: '',
    clientPhone: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.workName) {
      toast({
        title: 'שגיאה',
        description: 'נא למלא שם עבודה',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.clientName) {
      toast({
        title: 'שגיאה',
        description: 'נא למלא שם מזמין',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Save to backend
      const savedWork = await addWork(formData);
      
      toast({
        title: 'הצלחה!',
        description: 'דף העבודה נשמר בהצלחה'
      });
      
      // Navigate to work details page with the saved data
      navigate('/work-details', { state: { workData: savedWork } });
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: 'אירעה שגיאה בשמירת הנתונים',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50" dir="rtl">
      <div className="container mx-auto px-4 py-16">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800">דף עבודה חדש</h1>
          <Button
            onClick={() => navigate('/work')}
            variant="outline"
            className="border-2"
          >
            <ArrowRight className="ml-2 w-5 h-5" />
            חזרה לרשימה
          </Button>
        </div>

        {/* Form */}
        <div className="max-w-4xl mx-auto">
          <Card className="shadow-xl">
            <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">
              <CardTitle className="text-2xl">פרטי דף עבודה</CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Work Name */}
                <div>
                  <Label htmlFor="workName" className="text-lg font-semibold">
                    שם עבודה *
                  </Label>
                  <Input
                    id="workName"
                    type="text"
                    value={formData.workName}
                    onChange={(e) => setFormData({ ...formData, workName: e.target.value })}
                    placeholder="הזן שם עבודה"
                    className="mt-2 text-lg"
                  />
                </div>

                {/* Client Name */}
                <div>
                  <Label htmlFor="clientName" className="text-lg font-semibold">
                    שם מזמין *
                  </Label>
                  <Input
                    id="clientName"
                    type="text"
                    value={formData.clientName}
                    onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                    placeholder="הזן שם מזמין"
                    className="mt-2 text-lg"
                  />
                </div>

                {/* Client Phone */}
                <div>
                  <Label htmlFor="clientPhone" className="text-lg font-semibold">
                    מספר טלפון
                  </Label>
                  <Input
                    id="clientPhone"
                    type="tel"
                    value={formData.clientPhone}
                    onChange={(e) => setFormData({ ...formData, clientPhone: e.target.value })}
                    placeholder="הזן מספר טלפון"
                    className="mt-2 text-lg"
                    dir="ltr"
                  />
                </div>

                {/* Date */}
                <div>
                  <Label htmlFor="date" className="text-lg font-semibold">
                    תאריך
                  </Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="mt-2 text-lg"
                  />
                </div>

                {/* Description */}
                <div>
                  <Label htmlFor="description" className="text-lg font-semibold">
                    תיאור
                  </Label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="הזן תיאור (אופציונלי)"
                    className="mt-2 w-full min-h-[150px] px-3 py-2 text-lg border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* Submit Button */}
                <div className="flex justify-end gap-4 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/work')}
                    size="lg"
                  >
                    ביטול
                  </Button>
                  <Button
                    type="submit"
                    className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
                    size="lg"
                  >
                    <Save className="ml-2 w-5 h-5" />
                    שמור
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default WorkForm;
